import { load } from "@cashfreepayments/cashfree-js";
import AppConfig from "../../config/config";

// The @cashfreepayments/cashfree-js package is a thin loader that injects
// sdk.cashfree.com/js/v3/cashfree.js at runtime. The actual SDK exposes
// subscriptionsCheckout() which the npm types don't include — hence the cast.
type CashfreeWithSubs = {
  subscriptionsCheckout: (options: {
    subsSessionId: string;
    redirectTarget?: string;
  }) => Promise<{ error?: { message: string } }>;
};

// Module-level guard: prevents a second checkout call while one is already
// in-flight (e.g. double-tap after the Redux loading flag clears but before
// the page navigates away with _self).
let checkoutInFlight = false;

async function getInstance(): Promise<CashfreeWithSubs> {
  const mode = AppConfig.environment === "production" ? "production" : "sandbox";
  // Race against a timeout — a hanging script injection (some ad-blocker/CSP
  // setups stall rather than reject) would otherwise leave the button idle
  // forever with no user-visible feedback.
  const instance = await Promise.race([
    load({ mode }),
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error("Payment system timed out. Please refresh and try again.")),
        8000
      )
    ),
  ]);
  if (!instance) throw new Error("Payment system unavailable. Please refresh and try again.");
  return instance as unknown as CashfreeWithSubs;
}

/**
 * Verifies the Cashfree SDK can load. Call this BEFORE creating a backend
 * subscription — if the SDK is blocked (ad-blocker, CSP, network blip) we
 * fail fast here instead of leaving the customer with a dangling
 * ACTION_REQUIRED subscription they can't complete without a page reload.
 */
export async function verifyCashfreeAvailable(): Promise<void> {
  await getInstance();
}

/**
 * Opens the Cashfree subscription payment flow (_self redirect).
 *
 * Why _self and not _modal: "_modal" breaks inside the Android app's WebView. Android app builds
 * before MIN_ANDROID_APP_VERSION_FOR_AUTOPAY (platform.ts) send Cashfree's redirect to the phone's
 * browser, which has no session cookie ("subs_session_id is not present or is invalid"); newer
 * builds keep the redirect in the WebView. Do not reintroduce "_modal" without testing in the app.
 * Throws if the session is invalid/expired or the SDK fails to load.
 * Guards against concurrent calls — a second call while one is in-flight
 * returns immediately without starting a new checkout.
 *
 * Note: with _self redirect the page navigates away before the promise
 * settles in this JS context. Re-polling after return is handled by
 * SubscriptionsProgram's mount-time fetchUserSubscriptions dispatch.
 */
export async function openCashfreeCheckout(sessionId: string): Promise<void> {
  if (!sessionId) throw new Error("No subscription session id provided.");
  if (checkoutInFlight) return;

  checkoutInFlight = true;
  try {
    const cashfree = await getInstance();
    const result = await cashfree.subscriptionsCheckout({
      subsSessionId: sessionId,
      redirectTarget: "_self",
    });
    if (result?.error?.message) {
      throw new Error(result.error.message);
    }
  } finally {
    checkoutInFlight = false;
  }
}
